import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
    Alert, Button, Card, Checkbox, Col, DatePicker, Descriptions, Form, Input, InputNumber, Popconfirm, Row, Select, Space, Switch, Table, Tag, Typography, Upload, message
} from 'antd';
import { InboxOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import {
    api, asObj, AUDIT_ACTIONS, AUDIT_ENTITIES, errorText, formatChange, hasRole, money, qty, ruDate, ruDateTime, showError, useSub
} from './shared';
import CoefficientEditor from './CoefficientEditor';

const { Text } = Typography;

/* ---------------- Коэффициенты ---------------- */

export const CoefficientsTab = () => {
    const { me, settings } = useSub();
    const canEdit = hasRole(me, 'sdo');
    const [period, setPeriod] = useState(dayjs().startOf('month'));
    const [data, setData] = useState(null);
    const [history, setHistory] = useState([]);

    const load = useCallback(async () => {
        try {
            const [{ data: d }, { data: h }] = await Promise.all([
                api.get('/coefficients', { params: { period: period.format('YYYY-MM') } }),
                api.get('/audit', { params: { entity: 'coefficient', limit: 200 } })
            ]);
            setData(d);
            setHistory(h);
        } catch (error) {
            showError(error);
        }
    }, [period]);

    useEffect(() => {
        load();
    }, [load]);

    const cur = data?.current;
    return (
        <>
            <Card size="small" style={{ marginBottom: 12 }}>
                <Space wrap align="center">
                    <Text>Период:</Text>
                    <DatePicker picker="month" value={period} onChange={v => v && setPeriod(v)} format="MMMM YYYY" allowClear={false} />
                    <Text>Действующий коэффициент:</Text>
                    <Tag color="blue" style={{ fontSize: 16, padding: '2px 10px' }}>{cur?.value ?? '—'}</Tag>
                    {cur?.source === 'period' && <Text type="secondary">утверждён на этот месяц</Text>}
                    {cur?.source === 'inherited' && <Text type="secondary">унаследован с {cur.period}</Text>}
                    {cur?.source === 'default' && <Text type="secondary">по умолчанию</Text>}
                    {canEdit && <CoefficientEditor period={period.format('YYYY-MM')} current={cur?.value} settings={settings} onChanged={load} buttonText="Изменить коэффициент" />}
                </Space>
                <div style={{ marginTop: 8 }}>
                    <Text type="secondary">
                        По умолчанию {settings.default_coefficient}, допустимый диапазон {settings.coefficient_min}–{settings.coefficient_max}.
                        Новый месяц наследует коэффициент последнего утверждённого. Индивидуальный коэффициент субподрядчика задаётся в его карточке и имеет приоритет.
                    </Text>
                </div>
            </Card>
            <Row gutter={16}>
                <Col xs={24} lg={10}>
                    <Card size="small" title="Утверждённые коэффициенты">
                        <Table
                            size="small"
                            rowKey="id"
                            dataSource={data?.rows || []}
                            pagination={false}
                            columns={[
                                { title: 'Период', dataIndex: 'period' },
                                { title: 'Коэффициент', dataIndex: 'value', render: v => Number(v) },
                                { title: 'Основание', dataIndex: 'basis' },
                                { title: 'Изменён', dataIndex: 'updated_at', render: ruDateTime }
                            ]}
                        />
                    </Card>
                </Col>
                <Col xs={24} lg={14}>
                    <Card size="small" title="История изменений">
                        <Table
                            size="small"
                            rowKey="id"
                            dataSource={history}
                            pagination={{ pageSize: 20 }}
                            columns={[
                                { title: 'Дата', dataIndex: 'created_at', width: 140, render: ruDateTime },
                                { title: 'Пользователь', dataIndex: 'user_name' },
                                { title: 'Период', render: (_, r) => asObj(r.after).period },
                                { title: 'Было', render: (_, r) => asObj(r.before).value },
                                { title: 'Стало', render: (_, r) => <b>{asObj(r.after).value}</b> },
                                { title: 'Основание', dataIndex: 'comment' }
                            ]}
                        />
                    </Card>
                </Col>
            </Row>
        </>
    );
};

/* ---------------- Импорт Excel ---------------- */

const COLUMN_TITLES = {
    region: 'Регион',
    object_number: 'Номер объекта',
    object_address: 'Адрес объекта',
    request_number: 'Номер заявки',
    done_date: 'Дата выполнения',
    work_name: 'Наименование работы',
    executor: 'Исполнитель',
    quantity: 'Количество',
    price: 'Цена',
    amount: 'Сумма',
    customer: 'Заказчик',
    confirmed: 'Подтверждено заказчиком'
};

export const ImportTab = () => {
    const { me } = useSub();
    const canEdit = hasRole(me, 'sdo');
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [batches, setBatches] = useState([]);

    const loadBatches = useCallback(async () => {
        try {
            const { data } = await api.get('/imports');
            setBatches(data);
        } catch (error) {
            showError(error);
        }
    }, []);

    useEffect(() => {
        loadBatches();
    }, [loadBatches]);

    const send = async dryRun => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post(`/import?dryRun=${dryRun ? 1 : 0}`, fd);
            if (dryRun) setPreview(data);
            else {
                message.success(`Загружено строк: ${data.rows_ok}. Работы появятся в «Формировании» за соответствующий период.`);
                setPreview(null);
                setFile(null);
                loadBatches();
            }
        } catch (error) {
            message.error(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    if (!canEdit) return <Alert type="info" showIcon message="Импорт доступен только СДО" />;

    return (
        <>
            <Card size="small" style={{ marginBottom: 12 }}>
                <Upload.Dragger
                    accept=".xlsx,.xls"
                    maxCount={1}
                    beforeUpload={f => {
                        setFile(f);
                        setPreview(null);
                        return false;
                    }}
                    onRemove={() => {
                        setFile(null);
                        setPreview(null);
                    }}
                    fileList={file ? [{ uid: '1', name: file.name, status: 'done' }] : []}
                >
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">Перетащите Excel с выполненными работами или нажмите для выбора</p>
                    <p className="ant-upload-hint">
                        Колонки определяются автоматически по заголовкам: регион, номер объекта, адрес, номер заявки, дата выполнения, наименование работы,
                        исполнитель, количество, цена, сумма, заказчик, подтверждено заказчиком (если колонки нет — строки считаются подтверждёнными).
                    </p>
                </Upload.Dragger>
                <Space style={{ marginTop: 12 }}>
                    <Button disabled={!file} loading={busy} onClick={() => send(true)}>Проверить файл</Button>
                    <Button type="primary" disabled={!preview || !preview.rows_ok} loading={busy} onClick={() => send(false)}>
                        Загрузить {preview ? `${preview.rows_ok} строк` : ''}
                    </Button>
                </Space>
            </Card>

            {preview && (
                <Card size="small" title={`Предпросмотр: ${preview.file_name}`} style={{ marginBottom: 12 }}>
                    <Descriptions size="small" column={4} style={{ marginBottom: 8 }}>
                        <Descriptions.Item label="Лист">{preview.sheet}</Descriptions.Item>
                        <Descriptions.Item label="Строка заголовков">{preview.header_row}</Descriptions.Item>
                        <Descriptions.Item label="Строк всего">{preview.rows_total}</Descriptions.Item>
                        <Descriptions.Item label="К загрузке"><b>{preview.rows_ok}</b></Descriptions.Item>
                        <Descriptions.Item label="С ошибками"><Text type={preview.rows_error ? 'danger' : undefined}>{preview.rows_error}</Text></Descriptions.Item>
                        <Descriptions.Item label="Дубли (уже загружены)"><Text type={preview.rows_duplicate ? 'warning' : undefined}>{preview.rows_duplicate}</Text></Descriptions.Item>
                    </Descriptions>
                    <div style={{ marginBottom: 8 }}>
                        Распознаны колонки:{' '}
                        {Object.entries(preview.columns || {}).map(([k, v]) => <Tag key={k}>{COLUMN_TITLES[k] || k} ← «{v}»</Tag>)}
                    </div>
                    {preview.missing_columns?.length > 0 && (
                        <Alert type="warning" showIcon style={{ marginBottom: 8 }}
                            message={`Не найдены колонки: ${preview.missing_columns.map(k => COLUMN_TITLES[k] || k).join(', ')}. Такие работы попадут в «Ошибки / требует уточнения».`} />
                    )}
                    <Table
                        size="small"
                        rowKey="excel_row"
                        dataSource={preview.preview}
                        pagination={false}
                        scroll={{ x: 1600, y: 400 }}
                        columns={[
                            { title: 'Стр.', dataIndex: 'excel_row', width: 55 },
                            {
                                title: 'Проверка',
                                width: 150,
                                render: (_, r) => (r.duplicate ? <Tag color="orange">дубль</Tag> : r.errors?.length ? r.errors.map(e => <Tag key={e} color="red">{e}</Tag>) : <Tag color="green">ок</Tag>)
                            },
                            { title: 'Регион', dataIndex: 'region', width: 120 },
                            { title: 'Объект', dataIndex: 'object_number', width: 100 },
                            { title: 'Заявка', dataIndex: 'request_number', width: 120 },
                            { title: 'Дата', dataIndex: 'done_date', width: 95, render: ruDate },
                            { title: 'Работа', dataIndex: 'work_name', ellipsis: true },
                            { title: 'Исполнитель', dataIndex: 'executor', width: 150 },
                            { title: 'Кол-во', dataIndex: 'quantity', width: 70, align: 'right', render: qty },
                            { title: 'Цена', dataIndex: 'price', width: 90, align: 'right', render: money },
                            { title: 'Сумма', dataIndex: 'amount', width: 100, align: 'right', render: money },
                            { title: 'Заказчик', dataIndex: 'customer', width: 160 },
                            { title: 'Подтв.', dataIndex: 'confirmed', width: 70, render: v => (v ? 'да' : 'нет') }
                        ]}
                    />
                </Card>
            )}

            <Card size="small" title="Загрузки">
                <Table
                    size="small"
                    rowKey="id"
                    dataSource={batches}
                    pagination={{ pageSize: 20 }}
                    columns={[
                        { title: '№', dataIndex: 'id', width: 60 },
                        { title: 'Файл', dataIndex: 'file_name' },
                        { title: 'Строк', dataIndex: 'rows_ok', width: 80 },
                        { title: 'Активных', dataIndex: 'active_rows', width: 90 },
                        { title: 'Дата', dataIndex: 'created_at', width: 140, render: ruDateTime },
                        { title: 'Статус', dataIndex: 'active', width: 110, render: v => (v === false ? <Tag>отключена</Tag> : <Tag color="green">активна</Tag>) },
                        {
                            title: '',
                            width: 120,
                            render: (_, r) => (r.active !== false ? (
                                <Popconfirm title="Отключить загрузку? Строки, уже вошедшие в акты, останутся." onConfirm={async () => {
                                    try {
                                        await api.post(`/imports/${r.id}/deactivate`);
                                        loadBatches();
                                    } catch (error) {
                                        showError(error);
                                    }
                                }}>
                                    <Button size="small" danger type="link">Отключить</Button>
                                </Popconfirm>
                            ) : null)
                        }
                    ]}
                />
            </Card>
        </>
    );
};

/* ---------------- Журнал ---------------- */

export const AuditTab = () => {
    const [entity, setEntity] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/audit', { params: { entity: entity || undefined, limit: 2000 } });
            setRows(data);
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    }, [entity]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <>
            <Space style={{ marginBottom: 12 }}>
                <Select allowClear placeholder="Все объекты" style={{ width: 200 }} value={entity} onChange={setEntity}
                    options={Object.entries(AUDIT_ENTITIES).map(([value, label]) => ({ value, label }))} />
                <Button icon={<ReloadOutlined />} onClick={load}>Обновить</Button>
                <Text type="secondary">Удаление данных не предусмотрено — все изменения сохраняются здесь.</Text>
            </Space>
            <Table
                size="small"
                rowKey="id"
                loading={loading}
                dataSource={rows}
                pagination={{ pageSize: 50 }}
                columns={[
                    { title: 'Дата', dataIndex: 'created_at', width: 140, render: ruDateTime },
                    { title: 'Пользователь', dataIndex: 'user_name', width: 180 },
                    { title: 'Объект', dataIndex: 'entity', width: 130, render: (v, r) => `${AUDIT_ENTITIES[v] || v}${r.entity_id ? ` #${r.entity_id}` : ''}` },
                    { title: 'Действие', dataIndex: 'action', width: 170, render: v => AUDIT_ACTIONS[v] || v },
                    { title: 'Было', dataIndex: 'before', render: formatChange },
                    { title: 'Стало', dataIndex: 'after', render: formatChange },
                    { title: 'Комментарий', dataIndex: 'comment', width: 200 }
                ]}
            />
        </>
    );
};

/* ---------------- Настройки и роли ---------------- */

export const SettingsTab = ({ onChanged }) => {
    const { me, meta, settings } = useSub();
    const canEdit = hasRole(me, 'sdo');
    const [form] = Form.useForm();
    const [roles, setRoles] = useState(null);
    const [userSearch, setUserSearch] = useState('');

    useEffect(() => {
        form.setFieldsValue(settings);
    }, [settings, form]);

    const loadRoles = useCallback(async () => {
        if (!me.is_admin) return;
        try {
            const { data } = await api.get('/roles');
            setRoles(data);
        } catch (error) {
            showError(error);
        }
    }, [me.is_admin]);

    useEffect(() => {
        loadRoles();
    }, [loadRoles]);

    const save = async values => {
        try {
            await api.put('/settings', values);
            message.success('Настройки сохранены');
            onChanged?.();
        } catch (error) {
            showError(error);
        }
    };

    const setUserRoles = async (userId, list) => {
        try {
            await api.put(`/roles/${userId}`, { roles: list });
            message.success('Роли сохранены');
            loadRoles();
        } catch (error) {
            showError(error);
        }
    };

    const users = (roles?.users || []).filter(u => {
        if (!userSearch) return true;
        return `${u.last_name || ''} ${u.first_name || ''} ${u.login || ''}`.toLowerCase().includes(userSearch.toLowerCase());
    });

    return (
        <Row gutter={16}>
            <Col xs={24} xl={10}>
                <Card size="small" title="Настройки модуля">
                    <Form form={form} layout="vertical" disabled={!canEdit} onFinish={save}>
                        <Row gutter={12}>
                            <Col span={8}><Form.Item name="default_coefficient" label="Коэф. по умолчанию"><InputNumber min={0.5} max={1} step={0.01} style={{ width: '100%' }} /></Form.Item></Col>
                            <Col span={8}><Form.Item name="coefficient_min" label="Минимум"><InputNumber min={0.5} max={1} step={0.01} style={{ width: '100%' }} /></Form.Item></Col>
                            <Col span={8}><Form.Item name="coefficient_max" label="Максимум"><InputNumber min={0.5} max={1.5} step={0.01} style={{ width: '100%' }} /></Form.Item></Col>
                        </Row>
                        <Form.Item name="contractor_name" label="Подрядчик (в акте)"><Input /></Form.Item>
                        <Row gutter={12}>
                            <Col span={12}><Form.Item name="contractor_short" label="Подрядчик — для сокращения"><Input /></Form.Item></Col>
                            <Col span={12}><Form.Item name="city" label="Город составления акта"><Input /></Form.Item></Col>
                        </Row>
                        <Form.Item name="customer_prefix" label="Префикс заказчика (регион = заказчик без префикса)"><Input /></Form.Item>
                        <Typography.Title level={5}>Подтверждение заказчиком</Typography.Title>
                        <Form.Item name="confirm_by_customer_order" valuePropName="checked" style={{ marginBottom: 4 }}>
                            <Checkbox>Работа есть в заказе покупателя</Checkbox>
                        </Form.Item>
                        <Form.Item name="confirm_by_customer_act" valuePropName="checked" style={{ marginBottom: 4 }}>
                            <Checkbox>Работа есть в акте покупателя</Checkbox>
                        </Form.Item>
                        <Form.Item name="confirm_statuses" label="…или статус заявки">
                            <Select mode="multiple" allowClear options={(meta.task_statuses || []).map(s => ({ value: s, label: s }))} />
                        </Form.Item>
                        <Form.Item name="request_fallback_external_id" valuePropName="checked" label="Если нет номера ТТ — использовать «CRM <номер заявки>»">
                            <Switch />
                        </Form.Item>
                        {canEdit && <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>Сохранить</Button>}
                    </Form>
                </Card>
            </Col>
            <Col xs={24} xl={14}>
                <Card size="small" title="Роли модуля">
                    <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
                        <Descriptions.Item label="СДО">формирует акты, ведёт справочники, коэффициенты, импорт, передаёт в бухгалтерию</Descriptions.Item>
                        <Descriptions.Item label="Руководитель">утверждает акты (или возвращает)</Descriptions.Item>
                        <Descriptions.Item label="Бухгалтер">отмечает подписание, закрытие и оплату; объёмы не меняет</Descriptions.Item>
                        <Descriptions.Item label="Директор">просмотр сводки и финансов</Descriptions.Item>
                        <Descriptions.Item label="Без роли">просмотр (при доступе к странице «Субподряды»)</Descriptions.Item>
                    </Descriptions>
                    <div style={{ marginBottom: 8 }}>Ваши роли: {(me.roles || []).map(r => <Tag key={r} color="blue">{me.role_titles?.[r] || r}</Tag>)}{!me.roles?.length && <Text type="secondary">нет (только просмотр)</Text>}</div>
                    {me.is_admin ? (
                        <>
                            <Input.Search allowClear placeholder="Поиск сотрудника" style={{ width: 280, marginBottom: 8 }} onSearch={setUserSearch} onChange={e => !e.target.value && setUserSearch('')} />
                            <Table
                                size="small"
                                rowKey="id"
                                dataSource={users}
                                pagination={{ pageSize: 15 }}
                                columns={[
                                    { title: 'Сотрудник', render: (_, u) => <>{[u.last_name, u.first_name].filter(Boolean).join(' ') || u.login}{u.role_id === 1 && <Tag style={{ marginLeft: 6 }}>админ — все роли</Tag>}</> },
                                    {
                                        title: 'Роли в модуле',
                                        render: (_, u) => (
                                            <Checkbox.Group
                                                value={u.sub_roles}
                                                disabled={u.role_id === 1}
                                                onChange={list => setUserRoles(u.id, list)}
                                                options={(roles?.roles || []).map(r => ({ value: r.key, label: r.title }))}
                                            />
                                        )
                                    }
                                ]}
                            />
                        </>
                    ) : <Text type="secondary">Назначать роли может администратор CRM.</Text>}
                </Card>
            </Col>
        </Row>
    );
};
